import { normalize } from './index.js';

// Shoppers type what they say out loud: initials for long names, digits for spelled-out numbers, and
// the odd slip of a finger. These helpers turn that into terms the catalogue can be matched against.

const NUMBER_WORDS: Record<string, string> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
  eleven: '11',
  twelve: '12',
  thirteen: '13',
};
const DIGIT_WORDS: Record<string, string> = Object.fromEntries(
  Object.entries(NUMBER_WORDS).map(([word, digit]) => [digit, word]),
);

// Words that describe any fragrance rather than a particular one; matching on them is noise.
const CONCENTRATION_SYNONYMS: Record<string, string> = {
  edp: 'eau de parfum',
  edt: 'eau de toilette',
  edc: 'eau de cologne',
  'eau de parfum': 'edp',
  'eau de toilette': 'edt',
  'eau de cologne': 'edc',
  aftershave: 'eau de toilette',
  'after shave': 'eau de toilette',
  extrait: 'extrait de parfum',
  'pure parfum': 'extrait de parfum',
};

/** "Green Irish Tweed" is GIT and "Baccarat Rouge 540" is BR540: initials, with numbers kept whole. */
export function acronym(phrase: string) {
  const words = normalize(phrase).split(' ').filter(Boolean);
  if (words.length < 2) return '';
  const letters = words.map((word) => (/^\d+$/.test(word) ? word : word[0])).join('');
  return letters.length >= 2 && letters.length <= 8 ? letters : '';
}

/** Digits and their words are the same fragrance: 1 Million is also One Million. */
export function numberVariants(term: string) {
  const words = term.split(' ');
  const digits = words.map((word) => NUMBER_WORDS[word] ?? word).join(' ');
  const spelled = words.map((word) => DIGIT_WORDS[word] ?? word).join(' ');
  return [...new Set([term, digits, spelled])];
}

/** Every spelling of a search that is worth trying against the catalogue. */
export function expandQuery(query: string) {
  const term = normalize(query);
  if (!term) return [];
  const variants = new Set(numberVariants(term));
  const synonym = CONCENTRATION_SYNONYMS[term];
  if (synonym) variants.add(synonym);
  for (const [abbreviation, full] of Object.entries(CONCENTRATION_SYNONYMS)) {
    if ((' '+term+' ').includes(' '+abbreviation+' '))
      variants.add((' '+term+' ').replaceAll(' '+abbreviation+' ', ' '+full+' ').trim());
  }
  // No initials are generated from the query: the catalogue already stores them, and turning a typed
  // phrase into two letters matches half the shelf.
  return [...variants].filter(Boolean);
}

/** The extra spellings stored with a fragrance so an abbreviation finds it without being typed in. */
export function searchTokens({
  brand,
  brandAliases = [],
  name,
  concentration = '',
  aliases = [],
}: {
  brand?: string;
  brandAliases?: string[];
  name: string;
  concentration?: string;
  aliases?: string[];
}) {
  const tokens = new Set<string>();
  const add = (value: string) => {
    const clean = normalize(value);
    if (clean) tokens.add(clean);
  };
  for (const value of [brand, ...brandAliases, name, concentration, ...aliases])
    if (value) add(value);
  for (const value of [name, ...aliases])
    for (const variant of numberVariants(normalize(value))) add(variant);
  // Initials for the name on its own, and for the house and name together, as both get typed.
  for (const value of [name, ...aliases]) {
    add(acronym(value));
    for (const house of [brand, ...brandAliases])
      if (house && acronym(house)) add(acronym(house) + ' ' + acronym(value));
  }
  for (const house of [brand, ...brandAliases]) if (house) add(acronym(house));
  return [...tokens].filter(Boolean);
}

/** Bounded Levenshtein: anything past the limit is not a typo, so stop counting. */
export function editDistance(a: string, b: string, limit = 2) {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      best = Math.min(best, current[j]);
    }
    if (best > limit) return limit + 1;
    previous = current;
  }
  return previous[b.length];
}

/** A longer word can carry a longer slip, but a short one has to be nearly right. */
export const typoAllowance = (word: string) => (word.length <= 3 ? 0 : word.length <= 5 ? 1 : 2);

export function defaultSearchSort(query: string) {
  if (!normalize(query)) return 'popular';
  return genderFromQuery(query) ? 'az' : 'relevance';
}

function searchWordDistance(a: string, b: string, limit: number) {
  // Adjacent swapped letters are one typing slip, including short names such as Acqua.
  if (a.length === b.length) {
    const differences = [...a].flatMap((letter, i) => letter === b[i] ? [] : [i]);
    if (differences.length === 2 && differences[1] === differences[0] + 1 &&
      a[differences[0]] === b[differences[1]] && a[differences[1]] === b[differences[0]]) return 1;
  }
  return editDistance(a, b, limit);
}

/** Lower scores rank first. Every query word must match; a matching brand alone
 * cannot admit an unrelated fragrance when the shopper also supplies a name. */
export function fragranceSearchRank(candidate: {
  name: string; brand: string | null; concentration: string; searchText: string; aliases: string[];
}, terms: string[]): number | null {
  const name = normalize(candidate.name), brand = normalize(candidate.brand ?? '');
  const concentration = normalize(candidate.concentration);
  const identities = [name, `${brand} ${name}`.trim(), `${name} ${concentration}`.trim(), `${brand} ${name} ${concentration}`.trim()];
  const aliases = candidate.aliases.map(normalize);
  const nameWords = name.split(' ').filter(Boolean);
  const words = [...new Set(normalize([name, brand, concentration, candidate.searchText, ...aliases].join(' ')).split(' ').filter(Boolean))];
  let best = Infinity;
  for (const term of terms) {
    if (identities.includes(term)) { best = Math.min(best, 0); continue; }
    if (aliases.includes(term)) { best = Math.min(best, 1); continue; }
    if (brand === term) { best = Math.min(best, 2); continue; }
    const queryWords = term.split(' ').filter(Boolean);
    let cost = 0, valid = true;
    for (const queryWord of queryWords) {
      let wordCost = Infinity;
      for (const word of words) {
        if (word === queryWord) { wordCost = 0; break; }
        // Numbers distinguish releases (540, 724, 9 AM/PM); never fuzzy-match them.
        if (/\d/.test(queryWord) || /\d/.test(word)) continue;
        if (queryWord.length >= 2 && word.startsWith(queryWord)) wordCost = Math.min(wordCost, 1);
        const allowance = typoAllowance(queryWord);
        const distance = allowance ? searchWordDistance(word, queryWord, allowance) : Infinity;
        if (distance <= allowance) wordCost = Math.min(wordCost, 2 + distance);
      }
      if (!Number.isFinite(wordCost)) { valid = false; break; }
      cost += wordCost;
    }
    if (valid) {
      // Whole-name typo matches beat longer flankers containing the same word.
      const nameDistance = editDistance(name, term, typoAllowance(term));
      const wholeNameTypo = nameDistance <= typoAllowance(term);
      best = Math.min(best, 10 + cost * 10 + (wholeNameTypo ? 0 : 1) + Math.min(nameWords.length, 20) / 100);
    }
  }
  return Number.isFinite(best) ? best : null;
}

export type SearchGender = 'male' | 'female' | 'unisex';

const GENDER_ALIASES: Record<SearchGender, string[]> = {
  male: ['male', 'males', 'man', 'men', 'men s', 'masculine', 'him', 'for him', 'homme'],
  female: [
    'female',
    'females',
    'woman',
    'women',
    'women s',
    'lady',
    'ladies',
    'feminine',
    'her',
    'for her',
    'femme',
  ],
  unisex: [
    'unisex',
    'uni sex',
    'gender neutral',
    'genderless',
    'neutral',
    'all genders',
    'everyone',
    'for everyone',
  ],
};

const GENDER_SEARCH_FILLER = new Set([
  'aftershave',
  'aftershaves',
  'cologne',
  'colognes',
  'find',
  'for',
  'fragrance',
  'fragrances',
  'me',
  'perfume',
  'perfumes',
  'scent',
  'scents',
  'show',
]);

/**
 * Treat an audience term entered in the main search box as a gender filter. Filler words allow
 * natural searches such as "perfume for women", while requiring the whole remaining query to be
 * an audience term keeps fragrance names containing words such as "man" searchable as names.
 */
export function genderFromQuery(query: string): SearchGender | '' {
  const term = normalize(query);
  if (!term) return '';
  const candidates = [
    term,
    term
      .split(' ')
      .filter((word) => !GENDER_SEARCH_FILLER.has(word))
      .join(' '),
  ];
  for (const candidate of new Set(candidates)) {
    if (!candidate) continue;
    for (const [gender, aliases] of Object.entries(GENDER_ALIASES) as [SearchGender, string[]][])
      if (aliases.includes(candidate)) return gender;
    for (const [gender, aliases] of Object.entries(GENDER_ALIASES) as [SearchGender, string[]][]) {
      // Short audience words must be exact; longer ones can safely tolerate one or two slips.
      if (
        candidate.length >= 4 &&
        aliases.some(
          (alias) =>
            alias.length >= 4 &&
            editDistance(candidate, alias, typoAllowance(alias)) <= typoAllowance(alias),
        )
      )
        return gender;
    }
  }
  return '';
}

/** Does `candidate` contain `query`, allowing for a mistyped letter? */
export function fuzzyMatches(candidate: string, query: string) {
  if (!query) return true;
  if (candidate.includes(query)) return true;
  const limit = typoAllowance(query);
  if (!limit) return false;
  const words = candidate.split(' ');
  for (const word of words) if (editDistance(word, query, limit) <= limit) return true;
  // A query of several words is matched against the same span of the candidate.
  for (let i = 0; i < words.length; i++) {
    for (let j = i + 1; j <= Math.min(words.length, i + query.split(' ').length + 1); j++) {
      if (editDistance(words.slice(i, j).join(' '), query, limit) <= limit) return true;
    }
  }
  return false;
}
